import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { z } from "zod";
import {
  CA_POSTAL_RE,
  CreateDealershipSchema,
  CreateRooftopSchema,
  CreateUserSchema,
  DealershipCountrySchema,
  DealershipStatusSchema,
  EinSchema,
  StateOrProvinceCodeSchema,
  US_ZIP_RE,
  ZipOrPostalCodeSchema,
  countryLabel,
  stateOptionsForCountry,
  timezonesForCountry,
  type OnboardingCreateDto,
} from "@lumika/shared";
import {
  Button,
  FormField,
  Input,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../api/admin";
import { ApiError } from "../api/client";

// Mirrored from `CreateRooftopSchema`'s strict primitives. The form
// accepts "" so partial input doesn't trip the regex; submit-time
// `nullable()` converts to null which the server schema treats as
// "unset". A non-empty value must match the strict shape OR the
// user sees an inline error instead of a 400.
const STEPS = [
  { key: "identity" as const, label: "Identity", description: "Name, status." },
  { key: "rooftop" as const, label: "First rooftop", description: "Where the dealership operates." },
  { key: "owner" as const, label: "Owner user", description: "Optional — invite the first operator." },
];

// Plain object schema so step validation can `.pick()` field subsets;
// the exported `WizardSchema` layers the country-aware cross-field
// rules on top (a ZodEffects, which has no `.pick`).
const WizardBaseSchema = z.object({
  dealershipName: z.string().trim().min(1, "Dealership name is required.").max(200),
  legalName: z.string().trim().max(200).optional().or(z.literal("")),
  ein: z.union([z.literal(""), EinSchema]).optional(),
  status: DealershipStatusSchema,
  country: DealershipCountrySchema,
  primaryTimezone: z.string().trim().min(1),

  rooftopName: z.string().trim().min(1, "Rooftop name is required.").max(200),
  websiteUrl: z
    .union([z.literal(""), z.string().url("Use a full URL, e.g. https://example.com.")])
    .optional(),
  addressLine1: z.string().trim().max(160).optional().or(z.literal("")),
  city: z.string().trim().max(80).optional().or(z.literal("")),
  state: z.union([z.literal(""), StateOrProvinceCodeSchema]).optional(),
  zip: z.union([z.literal(""), ZipOrPostalCodeSchema]).optional(),

  inviteOwner: z.boolean(),
  userEmail: z.string().email("Invalid email.").or(z.literal("")).optional(),
  userFirstName: z.string().trim().max(80).optional().or(z.literal("")),
  userLastName: z.string().trim().max(80).optional().or(z.literal("")),
  userJobTitle: z.string().trim().max(80).optional().or(z.literal("")),
});

type WizardValues = z.infer<typeof WizardBaseSchema>;

// The base fields accept either country's shape (so nothing 400s at the
// server, which stores the union); the dealership's chosen country
// narrows state + zip to that country's vocabulary here.
function applyCountryRules(values: WizardValues, ctx: z.RefinementCtx) {
  const isCa = values.country === "CA";
  if (values.state) {
    const codes = new Set(stateOptionsForCountry(values.country).map((s) => s.code));
    if (!codes.has(values.state)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["state"],
        message: isCa ? "Pick a Canadian province." : "Pick a US state.",
      });
    }
  }
  if (values.zip && !(isCa ? CA_POSTAL_RE : US_ZIP_RE).test(values.zip)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["zip"],
      message: isCa
        ? "Use a Canadian postal code, e.g. A1A 1A1."
        : "Use ZIP (12345) or ZIP+4 (12345-6789).",
    });
  }
}

export const WizardSchema = WizardBaseSchema.superRefine(applyCountryRules);

const STEP_FIELDS: Record<(typeof STEPS)[number]["key"], (keyof WizardValues)[]> = {
  identity: [
    "dealershipName",
    "legalName",
    "ein",
    "status",
    "country",
    "primaryTimezone",
  ],
  rooftop: [
    "rooftopName",
    "websiteUrl",
    "addressLine1",
    "city",
    "state",
    "zip",
  ],
  owner: ["inviteOwner", "userEmail", "userFirstName", "userLastName", "userJobTitle"],
};

const INITIAL_VALUES: WizardValues = {
  dealershipName: "",
  legalName: "",
  ein: "",
  status: "onboarding",
  country: "US",
  primaryTimezone: "America/New_York",
  rooftopName: "",
  websiteUrl: "",
  addressLine1: "",
  city: "",
  state: "",
  zip: "",
  inviteOwner: false,
  userEmail: "",
  userFirstName: "",
  userLastName: "",
  userJobTitle: "",
};

function nullable(value: string | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed ? trimmed : null;
}

function displayName(first: string | undefined, last: string | undefined): string | null {
  const name = [first?.trim(), last?.trim()].filter(Boolean).join(" ");
  return name || null;
}

export function toOnboardingDto(values: WizardValues): OnboardingCreateDto {
  const dealership = CreateDealershipSchema.parse({
    name: values.dealershipName.trim(),
    legalName: nullable(values.legalName),
    ein: nullable(values.ein),
    status: values.status,
    country: values.country,
    primaryTimezone: values.primaryTimezone,
  });

  const rooftop = CreateRooftopSchema.omit({ dealershipId: true }).parse({
    name: values.rooftopName.trim(),
    status: "active",
    websiteUrl: nullable(values.websiteUrl),
    addressLine1: nullable(values.addressLine1),
    city: nullable(values.city),
    state: nullable(values.state),
    zip: nullable(values.zip),
    timezone: values.primaryTimezone,
    salesHours: {},
    serviceHours: {},
  });

  const users: OnboardingCreateDto["users"] = [];
  if (values.inviteOwner && values.userEmail) {
    const userBase = CreateUserSchema.omit({ dealershipId: true }).parse({
      email: values.userEmail,
      firstName: nullable(values.userFirstName),
      lastName: nullable(values.userLastName),
      displayName: displayName(values.userFirstName, values.userLastName),
      jobTitle: nullable(values.userJobTitle),
      kind: "human",
      status: "pending",
      roundRobinEnabled: true,
      notificationPrefs: {},
      metadata: {},
    });
    users.push({
      ...userBase,
      role: "dealership_owner",
      inboxVisibility: "group_all",
    });
  }

  return {
    dealership,
    rooftops: [rooftop],
    users,
  };
}

// Map a server validation/conflict error back onto the wizard's flat
// fields so it shows inline on the offending input instead of only a
// toast. Server issue paths are nested (e.g. ["rooftops", 0, "websiteUrl"]);
// collapse the array index and translate to the wizard field name.
const SERVER_PATH_TO_FIELD: Record<string, keyof WizardValues> = {
  "dealership.name": "dealershipName",
  "dealership.legalName": "legalName",
  "dealership.ein": "ein",
  "dealership.country": "country",
  "dealership.primaryTimezone": "primaryTimezone",
  "rooftops.name": "rooftopName",
  "rooftops.websiteUrl": "websiteUrl",
  "rooftops.addressLine1": "addressLine1",
  "rooftops.city": "city",
  "rooftops.state": "state",
  "rooftops.zip": "zip",
  "users.email": "userEmail",
  "users.firstName": "userFirstName",
  "users.lastName": "userLastName",
  "users.jobTitle": "userJobTitle",
};

export function mapServerErrorToFields(
  error: unknown,
): Partial<Record<keyof WizardValues, string>> | null {
  if (!(error instanceof ApiError)) return null;

  // nestjs-zod 400 body: `{ message: "Validation failed", errors: ZodIssue[] }`.
  const body = error.details;
  const issues =
    body && typeof body === "object" && Array.isArray((body as { errors?: unknown }).errors)
      ? (body as { errors: Array<{ path?: unknown; message?: unknown }> }).errors
      : null;
  if (error.status === 400 && issues) {
    const out: Partial<Record<keyof WizardValues, string>> = {};
    for (const issue of issues) {
      const path = Array.isArray(issue.path) ? issue.path : [];
      const key = path.filter((p) => typeof p === "string").join(".");
      const field = SERVER_PATH_TO_FIELD[key];
      if (field && !(field in out) && typeof issue.message === "string") {
        out[field] = issue.message;
      }
    }
    return Object.keys(out).length > 0 ? out : null;
  }

  // 409 conflict: a duplicate email — the message names the offending
  // value, so pin it to the matching field.
  if (error.status === 409) {
    if (/email/i.test(error.message)) return { userEmail: error.message };
  }
  return null;
}

function stepIndexForField(field: keyof WizardValues): number {
  for (let i = 0; i < STEPS.length; i += 1) {
    if (STEP_FIELDS[STEPS[i]!.key].includes(field)) return i;
  }
  return 0;
}

export function NewDealershipPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [stepIdx, setStepIdx] = useState(0);
  const form = useZodForm(WizardSchema, INITIAL_VALUES);

  const onboardMutation = useMutation({
    mutationFn: adminApi.onboard,
    onSuccess: async (detail) => {
      await qc.invalidateQueries({ queryKey: ["dealerships"] });
      toast.success(`Created ${detail.name}`);
      navigate(`/dealerships/${detail.id}/overview`);
    },
    onError: (error: Error) => {
      const fieldErrors = mapServerErrorToFields(error);
      if (fieldErrors) {
        form.setServerErrors(fieldErrors);
        const firstField = Object.keys(fieldErrors)[0] as keyof WizardValues | undefined;
        if (firstField) setStepIdx(stepIndexForField(firstField));
      }
      // Toast regardless so the failure is unmissable even when the
      // offending field lives on another step.
      toast.error(error.message);
    },
  });

  const currentStep = STEPS[stepIdx]!;
  const isLastStep = stepIdx === STEPS.length - 1;

  function validateStep(): boolean {
    const fields = STEP_FIELDS[currentStep.key];
    const subset = WizardBaseSchema.pick(
      Object.fromEntries(fields.map((f) => [f, true])) as Record<keyof WizardValues, true>,
    );
    const result = subset.safeParse(form.values);
    const errors: Partial<Record<keyof WizardValues, string>> = {};
    if (!result.success) {
      for (const issue of result.error.issues) {
        const head = issue.path[0];
        if (typeof head === "string" && !(head in errors)) {
          (errors as Record<string, string>)[head] = issue.message;
        }
      }
    }
    // The country rules cross step boundaries (country sits on the
    // identity step, state/zip on the rooftop step), so the subset
    // parse above never sees them — run the full-form rules and keep
    // only the issues that belong to this step's fields.
    const refined = WizardSchema.safeParse(form.values);
    if (!refined.success) {
      for (const issue of refined.error.issues) {
        const head = issue.path[0];
        if (
          typeof head === "string" &&
          fields.includes(head as keyof WizardValues) &&
          !(head in errors)
        ) {
          (errors as Record<string, string>)[head] = issue.message;
        }
      }
    }
    if (Object.keys(errors).length === 0) return true;
    form.setServerErrors(errors);
    return false;
  }

  function next() {
    if (!validateStep()) return;
    setStepIdx((idx) => Math.min(idx + 1, STEPS.length - 1));
  }

  function back() {
    setStepIdx((idx) => Math.max(idx - 1, 0));
  }

  function submit() {
    const result = form.validate();
    if (!result.ok) {
      const firstErrorKey = Object.keys(result.errors)[0];
      if (firstErrorKey) {
        for (let i = 0; i < STEPS.length; i += 1) {
          const stepKey = STEPS[i]!.key;
          if (STEP_FIELDS[stepKey].includes(firstErrorKey as keyof WizardValues)) {
            setStepIdx(i);
            break;
          }
        }
      }
      return;
    }
    if (result.data.inviteOwner && !result.data.userEmail) {
      form.setServerErrors({ userEmail: "Email is required when inviting an owner." });
      setStepIdx(STEPS.length - 1);
      return;
    }
    let dto: OnboardingCreateDto;
    try {
      dto = toOnboardingDto(result.data);
    } catch (err) {
      // The wizard passed but a strict server schema still rejected a
      // value. Surface it instead of letting the click die silently.
      toast.error(
        err instanceof z.ZodError
          ? (err.issues[0]?.message ?? "Some details are invalid.")
          : "Could not prepare the dealership. Please review your inputs.",
      );
      return;
    }
    onboardMutation.mutate(dto);
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Onboarding"
        title="New dealership"
        description="Create the dealership, its first rooftop, and (optionally) invite the first operator."
        actions={
          <Button
            variant="ghost"
            onClick={() => {
              const dirty =
                JSON.stringify(form.values) !== JSON.stringify(INITIAL_VALUES);
              if (dirty && !window.confirm("Discard this dealership?")) return;
              navigate("/dealerships");
            }}
          >
            Cancel
          </Button>
        }
      />

      <ol className="grid grid-cols-1 gap-2 rounded-lg border bg-card p-2 sm:grid-cols-3">
        {STEPS.map((step, idx) => {
          const status = idx < stepIdx ? "done" : idx === stepIdx ? "current" : "future";
          return (
            <li
              key={step.key}
              className={cn(
                "flex items-start gap-3 rounded-md p-3 transition-colors",
                status === "current" && "bg-accent",
                status === "done" && "text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                  status === "current"
                    ? "bg-primary text-primary-foreground"
                    : status === "done"
                      ? "bg-primary/15 text-primary"
                      : "border bg-background text-muted-foreground",
                )}
              >
                {status === "done" ? <Check className="h-3 w-3" /> : idx + 1}
              </span>
              <span className="flex flex-col">
                <span className="text-sm font-semibold leading-tight">{step.label}</span>
                <span className="text-xs text-muted-foreground">{step.description}</span>
              </span>
            </li>
          );
        })}
      </ol>

      {currentStep.key === "identity" && <IdentityStep form={form} />}
      {currentStep.key === "rooftop" && <RooftopStep form={form} />}
      {currentStep.key === "owner" && <OwnerStep form={form} />}

      <footer className="flex items-center justify-between">
        <Button variant="ghost" onClick={back} disabled={stepIdx === 0}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        {isLastStep ? (
          <Button onClick={submit} disabled={onboardMutation.isPending}>
            {onboardMutation.isPending ? "Creating…" : "Create dealership"}
          </Button>
        ) : (
          <Button onClick={next}>
            Next
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </footer>
    </div>
  );
}

type FormApi = ReturnType<typeof useZodForm<typeof WizardSchema>>;

function IdentityStep({ form }: { form: FormApi }) {
  const { values, errors, setField } = form;
  const timezones = timezonesForCountry(values.country);

  // Switching country invalidates the other country's vocabulary:
  // snap the timezone into the new country's list and clear the
  // state/province selection (codes don't carry across).
  function onCountryChange(country: WizardValues["country"]) {
    const nextTimezones = timezonesForCountry(country);
    form.patch({
      country,
      primaryTimezone: nextTimezones.includes(values.primaryTimezone)
        ? values.primaryTimezone
        : nextTimezones[0],
      state: "",
    });
  }

  return (
    <Section
      title="Identity"
      description="The dealership's display name and contact info."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Dealership name" required error={errors.dealershipName}>
          <Input
            value={values.dealershipName}
            onChange={(e) => setField("dealershipName", e.target.value)}
            placeholder="Sunshine Auto Group"
            autoFocus
          />
        </FormField>
        <FormField label="Legal name" optional error={errors.legalName}>
          <Input
            value={values.legalName ?? ""}
            onChange={(e) => setField("legalName", e.target.value)}
            placeholder="Sunshine Auto Holdings, LLC"
          />
        </FormField>
        <FormField label="EIN" optional error={errors.ein}>
          <Input
            value={values.ein ?? ""}
            onChange={(e) => setField("ein", e.target.value)}
            placeholder="Federal EIN (required for 10DLC brand)"
          />
        </FormField>
        <FormField label="Status" required error={errors.status}>
          <Select
            value={values.status}
            onValueChange={(v) => setField("status", v as WizardValues["status"])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DealershipStatusSchema.options.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Country" required error={errors.country}>
          <Select
            value={values.country}
            onValueChange={(v) => onCountryChange(v as WizardValues["country"])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DealershipCountrySchema.options.map((c) => (
                <SelectItem key={c} value={c}>
                  {countryLabel(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Primary timezone" required error={errors.primaryTimezone}>
          <Select
            value={values.primaryTimezone}
            onValueChange={(v) => setField("primaryTimezone", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timezones.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
    </Section>
  );
}

function RooftopStep({ form }: { form: FormApi }) {
  const { values, errors, setField } = form;
  const isCa = values.country === "CA";
  const stateOptions = stateOptionsForCountry(values.country);

  return (
    <Section
      title="First rooftop"
      description="Most dealerships have one rooftop to start. You can add more later."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Rooftop name" required error={errors.rooftopName}>
          <Input
            value={values.rooftopName}
            onChange={(e) => setField("rooftopName", e.target.value)}
            placeholder="Sunshine Auto — Downtown"
          />
        </FormField>
        <FormField className="sm:col-span-2" label="Website" optional error={errors.websiteUrl}>
          <Input
            type="url"
            value={values.websiteUrl ?? ""}
            onChange={(e) => setField("websiteUrl", e.target.value)}
            placeholder="https://sunshineauto.com"
          />
        </FormField>
        <FormField
          className="sm:col-span-2"
          label="Street address"
          optional
          error={errors.addressLine1}
        >
          <Input
            value={values.addressLine1 ?? ""}
            onChange={(e) => setField("addressLine1", e.target.value)}
            placeholder="123 Main St"
          />
        </FormField>
        <FormField label="City" optional error={errors.city}>
          <Input
            value={values.city ?? ""}
            onChange={(e) => setField("city", e.target.value)}
            placeholder="Austin"
          />
        </FormField>
        <FormField label={isCa ? "Province" : "State"} optional error={errors.state}>
          <Select
            value={values.state === "" ? "__none__" : (values.state ?? "__none__")}
            onValueChange={(v) =>
              setField("state", v === "__none__" ? "" : v)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={isCa ? "Pick a province" : "Pick a state"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {stateOptions.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={isCa ? "Postal code" : "ZIP"} optional error={errors.zip}>
          <Input
            value={values.zip ?? ""}
            onChange={(e) => setField("zip", e.target.value)}
            placeholder={isCa ? "A1A 1A1" : "12345 or 12345-6789"}
            maxLength={isCa ? 7 : 10}
          />
        </FormField>
      </div>
    </Section>
  );
}

function OwnerStep({ form }: { form: FormApi }) {
  const { values, errors, setField } = form;
  const enabled = values.inviteOwner;
  return (
    <Section
      title="Owner user"
      description="Optionally invite a first operator with dealership_owner role and full inbox visibility."
    >
      <label className="flex cursor-pointer items-center gap-3 rounded-md border p-3 hover:bg-muted/50">
        <input
          type="checkbox"
          className="h-4 w-4 rounded-sm border-input"
          checked={enabled}
          onChange={(e) => setField("inviteOwner", e.target.checked)}
        />
        <span className="text-sm">
          <strong>Invite an owner user now</strong>
          <span className="block text-xs text-muted-foreground">
            They'll show up in the dealership's user list with role <code>dealership_owner</code>.
          </span>
        </span>
      </label>
      <div
        className={cn("grid gap-4 sm:grid-cols-2", !enabled && "pointer-events-none opacity-50")}
      >
        <FormField
          className="sm:col-span-2"
          label="Email"
          required={enabled}
          error={errors.userEmail}
        >
          <Input
            type="email"
            disabled={!enabled}
            value={values.userEmail ?? ""}
            onChange={(e) => setField("userEmail", e.target.value)}
            placeholder="owner@sunshineauto.com"
          />
        </FormField>
        <FormField label="First name" optional error={errors.userFirstName}>
          <Input
            disabled={!enabled}
            value={values.userFirstName ?? ""}
            onChange={(e) => setField("userFirstName", e.target.value)}
            placeholder="Jamie"
          />
        </FormField>
        <FormField label="Last name" optional error={errors.userLastName}>
          <Input
            disabled={!enabled}
            value={values.userLastName ?? ""}
            onChange={(e) => setField("userLastName", e.target.value)}
            placeholder="Rivera"
          />
        </FormField>
        <FormField
          className="sm:col-span-2"
          label="Job title"
          optional
          error={errors.userJobTitle}
        >
          <Input
            disabled={!enabled}
            value={values.userJobTitle ?? ""}
            onChange={(e) => setField("userJobTitle", e.target.value)}
            placeholder="General Manager"
          />
        </FormField>
      </div>
    </Section>
  );
}
